import { transactionIdSetter } from "helpers/transactionIdFilter";
import moment from "moment";
import { useTranslation } from "react-i18next";

import gridIcon from "assets/icons/grid-icon.svg";
import Filter from "components/global/Filter/Filter";
import { FilterItemTypes } from "configs/types";
import { useAppSelector } from "store";

import "./CompensationsFilters.scss";

export interface FilterOption {
  value: string | number;
  label: string;
}

interface Props {
  operationId: string;
  setOperationId: (value: string) => void;
  userIds: string;
  setUserIds: (value: string) => void;
  transactionCode: string;
  setTransactionCode: (value: string) => void;
  playerNickname: string;
  setPlayerNickname: (value: string) => void;
  selectedOperators: FilterOption[];
  setSelectedOperators: (value: FilterOption[]) => void;
  operatorOptions: FilterOption[];
  amountFrom: string;
  setAmountFrom: (value: string) => void;
  amountTo: string;
  setAmountTo: (value: string) => void;
  selectedTypes: FilterOption[];
  setSelectedTypes: (value: FilterOption[]) => void;
  selectedStatuses: FilterOption[];
  setSelectedStatuses: (value: FilterOption[]) => void;
  selectedCurrencies: FilterOption[];
  setSelectedCurrencies: (value: FilterOption[]) => void;
  createdFrom: string;
  createdTo: string;
  setCreatedRange: (from: string, to: string) => void;
  handleApply: () => void;
}

const COMPENSATION_TYPES: FilterOption[] = [
  { value: "compensation", label: "Compensation" },
  { value: "reward", label: "Reward" },
  { value: "other_credit", label: "Other Credit" },
  { value: "balance_transfer_credit", label: "Balance Transfer Credit" },
  { value: "adjustment", label: "Adjustment" },
];

// There is no shared financial-operation status directory in the project yet.
// Keep the standard BO labels/codes used by the existing Payments screens.
const OPERATION_STATUSES: FilterOption[] = [
  { value: "created", label: "Created" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "declined", label: "Declined" },
  { value: "failed", label: "Failed" },
  { value: "canceled", label: "Canceled" },
];

const mergeSelection = (
  selected: FilterOption[],
  values: FilterOption[],
  isDelete?: boolean,
) => (isDelete ? values : [...selected, ...values]);

const CompensationsFilters: React.FC<Props> = ({
  operationId,
  setOperationId,
  userIds,
  setUserIds,
  transactionCode,
  setTransactionCode,
  playerNickname,
  setPlayerNickname,
  selectedOperators,
  setSelectedOperators,
  operatorOptions,
  amountFrom,
  setAmountFrom,
  amountTo,
  setAmountTo,
  selectedTypes,
  setSelectedTypes,
  selectedStatuses,
  setSelectedStatuses,
  selectedCurrencies,
  setSelectedCurrencies,
  createdFrom,
  createdTo,
  setCreatedRange,
  handleApply,
}) => {
  const { t } = useTranslation();
  const currencyCodes = useAppSelector((store) => store.settings.currencies);
  const currencies = currencyCodes.map((currency) => ({
    value: currency,
    label: currency,
  }));
  const isAmountCurrencyInvalid =
    Boolean(amountFrom.trim() || amountTo.trim()) &&
    selectedCurrencies.length !== 1;

  const handleClear = () => {
    setOperationId("");
    setUserIds("");
    setTransactionCode("");
    setPlayerNickname("");
    setSelectedOperators([]);
    setAmountFrom("");
    setAmountTo("");
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSelectedCurrencies([]);
    setCreatedRange("", "");
    handleApply();
  };

  return (
    <Filter
      className="compensations-filters"
      handleApply={handleApply}
      handleClear={handleClear}
      applyDisabled={isAmountCurrencyInvalid}
      filterItems={[
        {
          type: FilterItemTypes.input,
          title: "Operation ID",
          boxTitle: "Operation ID",
          icon: gridIcon,
          inputValue: operationId,
          setInputValue: transactionIdSetter(setOperationId),
        },
        {
          type: FilterItemTypes.input,
          title: "User ID",
          boxTitle: "User ID",
          icon: gridIcon,
          inputValue: userIds,
          setInputValue: transactionIdSetter(setUserIds, true),
        },
        {
          type: FilterItemTypes.input,
          title: "Transaction Code",
          boxTitle: "Transaction Code",
          icon: gridIcon,
          inputValue: transactionCode,
          setInputValue: setTransactionCode,
        },
        {
          type: FilterItemTypes.input,
          title: "Player Nickname",
          boxTitle: "Player Nickname",
          icon: gridIcon,
          inputValue: playerNickname,
          setInputValue: setPlayerNickname,
        },
        {
          type: FilterItemTypes.input,
          title: "Amount From",
          boxTitle: "Amount From",
          icon: gridIcon,
          inputType: "number",
          inputPlaceholder: "From",
          inputValue: amountFrom,
          setInputValue: setAmountFrom,
        },
        {
          type: FilterItemTypes.input,
          title: "Amount To",
          boxTitle: "Amount To",
          icon: gridIcon,
          inputType: "number",
          inputPlaceholder: "To",
          inputValue: amountTo,
          setInputValue: setAmountTo,
        },
        {
          type: FilterItemTypes.multiselect,
          title: "Admin",
          boxTitle: "Admin",
          icon: gridIcon,
          itemsArray: operatorOptions,
          activeItems: selectedOperators,
          handleApply: (values, isDelete) =>
            setSelectedOperators(
              mergeSelection(selectedOperators, values, isDelete),
            ),
        },
        {
          type: FilterItemTypes.multiselect,
          title: "Compensation Type",
          boxTitle: "Compensation Type",
          icon: gridIcon,
          itemsArray: COMPENSATION_TYPES,
          activeItems: selectedTypes,
          handleApply: (values, isDelete) =>
            setSelectedTypes(mergeSelection(selectedTypes, values, isDelete)),
        },
        {
          type: FilterItemTypes.multiselect,
          title: "Status",
          boxTitle: "Statuses filter",
          icon: gridIcon,
          itemsArray: OPERATION_STATUSES,
          activeItems: selectedStatuses,
          handleApply: (values, isDelete) =>
            setSelectedStatuses(
              mergeSelection(selectedStatuses, values, isDelete),
            ),
        },
        {
          type: FilterItemTypes.datePicker,
          title: "Created",
          boxTitle: "Created",
          icon: gridIcon,
          firstDatePlaceholder: "Created From",
          secondDatePlaceholder: "Created To",
          firstDateInitValue: createdFrom,
          secondDateInitValue: createdTo,
          handleApplyDate: (from, to) => setCreatedRange(from, to ?? ""),
          activeItems: [
            ...(createdFrom
              ? [{ value: "from", label: `From: ${createdFrom}` }]
              : []),
            ...(createdTo ? [{ value: "to", label: `To: ${createdTo}` }] : []),
          ],
          onDeleteItem: (value) =>
            setCreatedRange(
              value === "from" ? "" : createdFrom,
              value === "to" ? "" : createdTo,
            ),
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("currencies"),
          boxTitle: t("filterCurrencies"),
          icon: gridIcon,
          itemsArray: currencies,
          activeItems: selectedCurrencies,
          handleApply: (values, isDelete) =>
            setSelectedCurrencies(
              mergeSelection(selectedCurrencies, values, isDelete),
            ),
        },
      ]}
    />
  );
};

export const toIsoDateTime = (value: string) =>
  moment(value, "DD.MM.YYYY HH:mm", true).toISOString();

export default CompensationsFilters;
